import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '../api/client';
import { useApi } from './provider';

/**
 * Slice 13 — Calendar event queries + mutations.
 *
 * Date-bounded reads. The backend defaults to "current calendar month
 * UTC" when `from` / `to` are omitted, but every SPA caller should pass
 * an explicit range matching what the calendar surface is currently
 * displaying so the cache key reflects the visible window.
 *
 * Mutations (create / update / delete / reschedule / leave) all
 * invalidate the workspace + project event caches on settle. We
 * deliberately don't optimistically patch these like task drag does —
 * event payloads are larger (denormalized attendees) and the latency
 * cost of a re-fetch is ~50ms. Reschedule is the one exception (drag
 * gesture; optimistic feels mandatory) and is built like
 * `useRescheduleTask`.
 */

export interface CalendarEventRangeArgs {
  /** Inclusive start as `YYYY-MM-DD` or full ISO 8601. */
  from?: string;
  /** Inclusive end as `YYYY-MM-DD` or full ISO 8601. */
  to?: string;
}

export const calendarEventKeys = {
  byWorkspace: (workspaceSlug: string, args: CalendarEventRangeArgs = {}) =>
    [
      'calendar-events',
      'workspace',
      workspaceSlug,
      args.from ?? '',
      args.to ?? '',
    ] as const,
  byProject: (workspaceSlug: string, projectSlug: string, args: CalendarEventRangeArgs = {}) =>
    [
      'calendar-events',
      'project',
      workspaceSlug,
      projectSlug,
      args.from ?? '',
      args.to ?? '',
    ] as const,
  detail: (workspaceSlug: string, eventUlid: string) =>
    ['calendar-events', 'detail', workspaceSlug, eventUlid] as const,
};

export const workspaceCalendarEventsQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  args: CalendarEventRangeArgs = {},
) =>
  queryOptions({
    queryKey: calendarEventKeys.byWorkspace(workspaceSlug, args),
    queryFn: async () => {
      const query: Record<string, string> = {};
      for (const [k, v] of Object.entries(args)) {
        if (v !== undefined && v !== '') query[k] = v as string;
      }
      const { data, error } = await api.GET('/v1/workspaces/{workspace_slug}/calendar-events', {
        params: {
          path: { workspace_slug: workspaceSlug },
          query: query as never,
        },
      });
      if (error) throw error;
      return data;
    },
  });

export const useWorkspaceCalendarEvents = (
  workspaceSlug: string | undefined,
  args: CalendarEventRangeArgs = {},
) => {
  const api = useApi();
  return useQuery({
    ...workspaceCalendarEventsQueryOptions(api, workspaceSlug ?? '', args),
    enabled: Boolean(workspaceSlug),
  });
};

export const calendarEventsQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  projectSlug: string,
  args: CalendarEventRangeArgs = {},
) =>
  queryOptions({
    queryKey: calendarEventKeys.byProject(workspaceSlug, projectSlug, args),
    queryFn: async () => {
      const query: Record<string, string> = {};
      for (const [k, v] of Object.entries(args)) {
        if (v !== undefined && v !== '') query[k] = v as string;
      }
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/projects/{projectSlug}/calendar-events',
        {
          params: {
            path: { workspace_slug: workspaceSlug, projectSlug },
            query: query as never,
          },
        },
      );
      if (error) throw error;
      return data;
    },
  });

export const useCalendarEvents = (
  workspaceSlug: string | undefined,
  projectSlug: string | undefined,
  args: CalendarEventRangeArgs = {},
) => {
  const api = useApi();
  return useQuery({
    ...calendarEventsQueryOptions(api, workspaceSlug ?? '', projectSlug ?? '', args),
    enabled: Boolean(workspaceSlug && projectSlug),
  });
};

export const calendarEventQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  eventUlid: string,
) =>
  queryOptions({
    queryKey: calendarEventKeys.detail(workspaceSlug, eventUlid),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/calendar-events/{eventUlid}',
        {
          params: { path: { workspace_slug: workspaceSlug, eventUlid } },
        },
      );
      if (error) throw error;
      return data;
    },
  });

export const useCalendarEvent = (
  workspaceSlug: string | undefined,
  eventUlid: string | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...calendarEventQueryOptions(api, workspaceSlug ?? '', eventUlid ?? ''),
    enabled: Boolean(workspaceSlug && eventUlid),
  });
};

/* ─ Mutations ─────────────────────────────────────────────────────── */

export interface CreateCalendarEventInput {
  project_id?: string | null;
  title: string;
  description?: string | null;
  location?: string | null;
  /** ISO 8601 — full timestamp for timed events, `YYYY-MM-DD` for all-day. */
  start_at: string;
  /** Required when `is_all_day` is false; nullable for single-day all-day. */
  end_at?: string | null;
  is_all_day?: boolean;
  /** Workspace-member user ULIDs. The creator is always added by the
   *  backend; sending the creator's id here is harmless (de-duped). */
  attendee_ids?: string[];
}

export interface UpdateCalendarEventInput {
  project_id?: string | null;
  title?: string;
  description?: string | null;
  location?: string | null;
  start_at?: string;
  end_at?: string | null;
  is_all_day?: boolean;
  /** Full attendee-set replacement. Omit to leave attendees untouched.
   *  Sending an empty array leaves only the creator (backend preserves). */
  attendee_ids?: string[];
}

const invalidateEventCaches = (
  qc: ReturnType<typeof useQueryClient>,
  workspaceSlug: string,
  eventUlid?: string,
): Promise<void[]> =>
  Promise.all([
    qc.invalidateQueries({ queryKey: ['calendar-events', 'workspace', workspaceSlug] }),
    qc.invalidateQueries({ queryKey: ['calendar-events', 'project', workspaceSlug] }),
    eventUlid
      ? qc.invalidateQueries({ queryKey: calendarEventKeys.detail(workspaceSlug, eventUlid) })
      : Promise.resolve(),
  ]) as Promise<void[]>;

export const useCreateCalendarEvent = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCalendarEventInput) => {
      const { data, error } = await api.POST(
        '/v1/workspaces/{workspace_slug}/calendar-events',
        {
          params: { path: { workspace_slug: workspaceSlug } },
          // The spec generated from form-request rules doesn't model
          // nullable fields cleanly; cast to bypass narrowing — the
          // controller accepts the wider shape (covered by Pest tests).
          body: input as {
            project_id?: string;
            title: string;
            description?: string;
            location?: string;
            start_at: string;
            end_at?: string;
            is_all_day?: boolean;
            attendee_ids?: string[];
          },
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await invalidateEventCaches(qc, workspaceSlug);
    },
  });
};

export const useUpdateCalendarEvent = (workspaceSlug: string, eventUlid: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateCalendarEventInput) => {
      const { data, error } = await api.PATCH(
        '/v1/workspaces/{workspace_slug}/calendar-events/{eventUlid}',
        {
          params: { path: { workspace_slug: workspaceSlug, eventUlid } },
          body: input as {
            project_id?: string;
            title?: string;
            description?: string;
            location?: string;
            start_at?: string;
            end_at?: string;
            is_all_day?: boolean;
            attendee_ids?: string[];
          },
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await invalidateEventCaches(qc, workspaceSlug, eventUlid);
    },
  });
};

export const useDeleteCalendarEvent = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (eventUlid: string) => {
      const { data, error } = await api.DELETE(
        '/v1/workspaces/{workspace_slug}/calendar-events/{eventUlid}',
        {
          params: { path: { workspace_slug: workspaceSlug, eventUlid } },
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async (_data, eventUlid) => {
      await invalidateEventCaches(qc, workspaceSlug, eventUlid);
    },
  });
};

export interface RescheduleCalendarEventInput {
  /** ULID of the event being moved. Carried in the mutate input (not
   *  the hook factory) for the same reason as `useReorderTask` —
   *  drag fires once per gesture and we don't want a hook instance
   *  per chip on the calendar grid. */
  eventUlid: string;
  /** New start time as ISO 8601. For all-day events, send `YYYY-MM-DD`;
   *  for timed events, the SPA preserves the time-of-day from the
   *  original event when computing the new start_at. */
  start_at: string;
  /** Optional matching end_at — for timed events, the SPA shifts
   *  end_at by the same delta as start_at to preserve duration. */
  end_at?: string | null;
}

/**
 * Calendar drag-to-reschedule for events. Optimistic patching of the
 * workspace + project event-list caches so the chip snaps to its new
 * bucket without waiting for the server.
 *
 * The cache patching is shape-aware: every range-keyed events query
 * gets a `setQueryData` call that swaps `start_at` / `end_at` on the
 * matching row. Settle invalidates everything to reconcile.
 */
export const useRescheduleCalendarEvent = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RescheduleCalendarEventInput) => {
      const { eventUlid, start_at, end_at } = input;
      const body: { start_at: string; end_at?: string } = { start_at };
      if (end_at !== undefined && end_at !== null) {
        body.end_at = end_at;
      }
      const { data, error } = await api.PATCH(
        '/v1/workspaces/{workspace_slug}/calendar-events/{eventUlid}',
        {
          params: { path: { workspace_slug: workspaceSlug, eventUlid } },
          body,
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async (input) => {
      const wsPrefix = ['calendar-events', 'workspace', workspaceSlug] as const;
      const projPrefix = ['calendar-events', 'project', workspaceSlug] as const;
      await Promise.all([
        qc.cancelQueries({ queryKey: wsPrefix }),
        qc.cancelQueries({ queryKey: projPrefix }),
      ]);

      type EventListPayload = { data?: Array<{ id: string; attributes?: Record<string, unknown> }> };

      const previous: Array<[readonly unknown[], EventListPayload | undefined]> = [
        ...qc.getQueriesData<EventListPayload>({ queryKey: wsPrefix }),
        ...qc.getQueriesData<EventListPayload>({ queryKey: projPrefix }),
      ].map(([k, v]) => [k, v] as const);

      for (const [key, value] of previous) {
        if (!value?.data) continue;
        qc.setQueryData<EventListPayload>(key, {
          ...value,
          data: value.data.map((row) => {
            if (row.id !== input.eventUlid) return row;
            const next = { ...(row.attributes ?? {}) };
            next.start_at = input.start_at;
            if (input.end_at !== undefined) next.end_at = input.end_at;
            return { ...row, attributes: next };
          }),
        });
      }

      return { previous, eventUlid: input.eventUlid };
    },
    onError: (_err, _input, ctx) => {
      if (!ctx) return;
      for (const [key, value] of ctx.previous) {
        qc.setQueryData(key, value);
      }
    },
    onSettled: (_data, _err, _input, ctx) => {
      void invalidateEventCaches(qc, workspaceSlug, ctx?.eventUlid);
    },
  });
};

/**
 * Remove a single attendee from an event. Doubles as the "leave
 * event" action — when `userId` is the current user, the backend
 * accepts the request even from a non-creator member.
 */
export const useRemoveCalendarEventAttendee = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { eventUlid: string; userId: string }) => {
      const { eventUlid, userId } = input;
      const { data, error } = await api.DELETE(
        '/v1/workspaces/{workspace_slug}/calendar-events/{eventUlid}/attendees/{userId}',
        {
          params: { path: { workspace_slug: workspaceSlug, eventUlid, userId } },
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async (_data, input) => {
      await invalidateEventCaches(qc, workspaceSlug, input.eventUlid);
    },
  });
};
